import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';

import { RoutePath } from 'applications/GSP/shared/constants/router';
import { Loader } from 'shared/components/Loader';
import { Content as Wrapper, EmptyState } from 'shared/components/Tile';
import { BarChart } from 'shared/components/charts/BarChart';
import { isArrayEmpty } from 'shared/utils/common';
import { redirectTo } from 'shared/utils/router';

import { TooltipContent } from './components/TooltipContent';
import { useCumulativeGrades } from './hooks';

import type { CumulativeGradesProps } from 'applications/GSP/entities/CumulativeGrades/types';
import type { InspiredGlobalStudyPlatformApiModelsChartGradeType as GradeType } from 'applications/GSP/shared/api/gsp';
import type { FC } from 'react';
import type { OnInteract } from 'shared/components/charts/BarChart';

interface ContentProps extends CumulativeGradesProps {
  gradeType?: GradeType;
}

export const Content: FC<ContentProps> = ({
  data,
  isLoading,
  onInteract,
  gradeType,
}) => {
  const cumulativeGrades = useCumulativeGrades(data);
  const { t } = useTranslation();

  const handleInteract = useCallback<OnInteract>(
    subjectId => {
      if (subjectId) {
        redirectTo(
          generatePath(RoutePath.ProgressSubject, {
            subjectId,
          }),
        );

        onInteract();
      }
    },
    [onInteract],
  );

  if (isLoading) {
    return (
      <Wrapper>
        <Loader />
      </Wrapper>
    );
  }

  if (isArrayEmpty(cumulativeGrades)) {
    return (
      <Wrapper>
        <EmptyState
          header={t('GSP.entities.cumulativeGrades.emptyState.header')}
          description={t(
            'GSP.entities.cumulativeGrades.emptyState.description',
          )}
        />
      </Wrapper>
    );
  }

  if (!cumulativeGrades) {
    return null;
  }

  return (
    <Wrapper>
      <BarChart
        data={cumulativeGrades}
        yAxis={data?.yAxis}
        TooltipContent={TooltipContent}
        onInteract={handleInteract}
        gradeType={gradeType}
      />
    </Wrapper>
  );
};
